import React, { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import HeadingComponent from "components/headingComponent";
import { getDoc, doc, setDoc, Timestamp } from "firebase/firestore";
import ErrorMessages from "helper/errorMessages";
import TableName from "helper/tableName/tableName";
import { dbFirestore } from "helper/config/firebase";
import ToastMessages from "helper/toastMessages";
import { toast } from "react-toastify";
import CommonHeadings from "helper/constant/headings";

function Profile() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [profileData, setProfileData] = useState({
    name: "",
    business_name: "",
    phone: "",
    second_phone: "",
    email: "",
    second_email: "",
    address: "",
    role: "Admin",
    pinCode: "",
    gstNumber: "",
    city: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    business_name: "",
    address: "",
    pinCode: "",
    phone: "",
    email: "",
    city: "",
  });

  const inputChange = (e) => {
    const { name, value } = e.target;

    setProfileData({
      ...profileData,
      [name]: value,
    });
  };

  // MARK: use effect function
  useEffect(() => {
    getData();
  }, []);

   // use layout effect function
   useLayoutEffect(() => {
    document.title = CommonHeadings.profile;
  }, []);
  
  const validateForm = (data) => {
    const errors = {};

    if (!data.name) {
      errors.name = ErrorMessages.name;
    }
    if (!data.business_name) {
      errors.business_name = ErrorMessages.businessName;
    }
    if (!data.city) {
      errors.city = ErrorMessages.city;
    }
    if (!data.pinCode) {
      errors.pinCode = ErrorMessages.pinCode;
    }
    if (!data.phone) {
      errors.phone = ErrorMessages.phone;
    } else if (!/^\d*$/.test(data.phone)) {
      errors.phone = ErrorMessages.phoneNumber;
    }
    if (!data.email) {
      errors.email = ErrorMessages.email;
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = ErrorMessages.emailPattern;
    }
    if (!data.address) {
      errors.address = ErrorMessages.address;
    }
    return errors;
  };

  // MARK: save function
  const tapOnSave = (e) => {
    const newErrors = validateForm(profileData);
    setErrors(newErrors);
    setIsSubmitted(true);
    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      writeProfileApi();
    }
  };

  // MARK: payload function
  function payload() {
    let auth = localStorage.getItem("session");
    const userInfo = JSON.parse(auth);
    const createdAt = Timestamp.fromDate(new Date()).toMillis();
    var object = {
      ...profileData,
      created_at: createdAt,
      updated_at: createdAt,
      uid: userInfo.uid 
    };

    object.name = profileData.name.toLowerCase();
    object.business_name = profileData.business_name.toLowerCase();
    object.phone = profileData.phone;
    object.second_phone = profileData.second_phone;
    object.email = profileData.email;
    object.second_email = profileData.second_email;
    object.address = profileData.address.toLowerCase();
    object.role =  profileData.role.toLowerCase();
    object.pinCode = profileData.pinCode;
    object.gstNumber = profileData.gstNumber.toLowerCase();
    object.city = profileData.city.toLowerCase();

    return object;
  }

  //MARK: for getting a data from local storage
  async function getData() {
    setLoading(false);
    let auth = localStorage.getItem("session");
    const userInfo = JSON.parse(auth);

    getDoc(doc(dbFirestore, TableName.profile, userInfo.uid))
      .then((response) => {
        setProfileData((prevState) => ({
          ...prevState,
          name: response.data()?.name,
          phone: response.data()?.phone,
          second_phone: response.data()?.second_phone,
          email: response.data()?.email,
          second_email: response.data()?.second_email,
          city: response.data()?.city,
          address: response.data()?.address,
          role: response.data()?.role ?? "Admin",
          pinCode: response.data()?.pinCode,
          gstNumber: response.data()?.gstNumber,
          business_name: response.data()?.business_name,
        }));
      })
      .catch((error) => {
        toast.error(error);
      });
  }

  // for add data using setDoc which is used when we assign an ID to it, without ID it doesn't create any object.
  // Remeber that addDoc function create an function when we create any object, but setDoc doesn't create an any object without assigning value
  async function writeProfileApi() {
    let auth = localStorage.getItem("session");
    const userInfo = JSON.parse(auth);

    setDoc(doc(dbFirestore, TableName.profile, userInfo.uid), payload())
      .then(() => {
        setLoading(false);
        toast.success(ToastMessages.profileSuccess);
      })
      .catch((error) => {
        const errorMessage = error.message;
        toast.error(errorMessage);
        setLoading(false);
      });
  }

  return (
    <div className="modules__main__div">
      <div className="col-12">
        <div className="card modules__card">
          <HeadingComponent classname="mb-1 mt-3" headingName={CommonHeadings.profile} />

          <div className="p-fluid">
            <div className="row modules__row mt-4">
              <div className="col-12 col-md-6">
                <label htmlFor="name" className="input__valid">
                  Name
                </label>
                <InputText
                  name="name"
                  value={profileData.name}
                  onChange={inputChange}
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                />
                {isSubmitted && (
                  <small id="name" className="p-error">
                    {errors.name}{" "}
                  </small>
                )}
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="business_name" className="input__valid">
                  Business Name
                </label>
                <InputText
                  name="business_name"
                  value={profileData.business_name}
                  onChange={inputChange}
                  id="business_name"
                  type="text"
                  placeholder="Enter your business name"
                />
                {isSubmitted && (
                  <small id="business_name" className="p-error">
                    {errors.business_name}{" "}
                  </small>
                )}
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="phone" className="input__valid">
                  Primary Contact Number
                </label>
                <InputText
                  name="phone"
                  value={profileData.phone}
                  onChange={inputChange}
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  maxLength={10}
                />
                {isSubmitted && (
                  <small id="phone" className="p-error">
                    {errors.phone}{" "}
                  </small>
                )}
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="second_phone">Secondary Contact Number</label>
                <InputText
                  name="second_phone"
                  value={profileData.second_phone}
                  onChange={inputChange}
                  id="second_phone"
                  type="tel"
                  placeholder="Enter your another phone number"
                  maxLength={10}
                />
              </div>

              <div className="col-12 col-md-6">
                <label htmlFor="email" className="input__valid">
                  Primary Email
                </label>
                <InputText
                  name="email"
                  value={profileData.email}
                  onChange={inputChange}
                  id="email"
                  type="text"
                  placeholder="Enter your business email"
                />
                {isSubmitted && (
                  <small id="email" className="p-error">
                    {errors.email}{" "}
                  </small>
                )}
              </div>

              <div className="col-12 col-md-6">
                <label htmlFor="second_email">Secondary Email</label>
                <InputText
                  name="second_email"
                  value={profileData.second_email}
                  onChange={inputChange}
                  id="second_email"
                  type="text"
                  placeholder="Enter your another email"
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="city" className="input__valid">
                  City
                </label>
                <InputText
                  name="city"
                  value={profileData.city}
                  onChange={inputChange}
                  id="city"
                  type="text"
                  placeholder="Enter your city"
                />
                {isSubmitted && (
                  <small id="city" className="p-error">
                    {errors.city}{" "}
                  </small>
                )}
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="role" className="input__valid">
                  Role
                </label>
                <InputText
                  name="role"
                  value={profileData.role}
                  onChange={inputChange}
                  id="role"
                  type="role"
                  readOnly
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="pinCode" className="input__valid">
                  Pincode
                </label>
                <InputText
                  name="pinCode"
                  value={profileData.pinCode}
                  onChange={inputChange}
                  id="pinCode"
                  type="text"
                  placeholder="Enter pincode number"
                />
                {isSubmitted && (
                  <small id="pinCode" className="p-error">
                    {errors.pinCode}{" "}
                  </small>
                )}
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="gstNumber">GST Number</label>
                <InputText
                  name="gstNumber"
                  value={profileData.gstNumber}
                  onChange={inputChange}
                  id="gstNumber"
                  type="gstNumber"
                  placeholder="Enter your GST number"
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="address" className="input__valid">
                  Address
                </label>
                <InputTextarea
                  autoResize
                  name="address"
                  value={profileData.address}
                  onChange={inputChange}
                  rows={2}
                  cols={5}
                  id="address"
                  placeholder="Enter your address"
                />
                {isSubmitted && (
                  <small id="address" className="p-error">
                    {errors.address}{" "}
                  </small>
                )}
              </div>
            </div>
          </div>
          <div className="flex mt-3">
            <Button
              label="Save"
              loading={loading}
              type="button"
              onClick={tapOnSave}
              className="modules__save_button me-3"
            />
            <Button
              label="Back"
              severity="secondary"
              onClick={() => navigate(-1)}
              className="modules__back_button"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
